import { DeepMap, FieldError } from 'react-hook-form';
import {
	ErrorArgs,
	EventAction,
	EventArgs,
	EventCategory,
	EventKeys,
	EventLabel,
	FormErrors,
} from './analytics-interfaces';
import { getCustomKeyValues, replaceHtmlTags } from './analytics-helper';
import { IFormInput } from '../components/domains/forms/patient/enrollment.models';
import { EnrollmentContextService } from '../components/hoc/context/enrollment/enrollment-context';
import { Question } from '../hooks/useQuestions';
import { ConfigurationModel } from '../components/hoc/context/product/configuration-model';
import { TestOption, getABFlow } from '../utils/abFlow';
import abTestEnabled from '../components/utilities/abTestEnabled';

export type ABFLowGA = TestOption | 'none';

export function getABFlowGA(): ABFLowGA {
	if (!abTestEnabled) {
		return 'none';
	}
	return getABFlow();
}

export default abstract class AbstractAnalytics {
	abstract push(eventArgs: EventArgs): void;
	abstract getEventName(): string;

	public sendEvent(eventArgs: EventArgs): void {
		const hasPageRequestTime = eventArgs.hasOwnProperty(
			EventKeys.PageRequestTime
		);

		const dL = hasPageRequestTime
			? eventArgs
			: Object.assign(
					{},
					{ event: this.getEventName(), pageURL: `${window?.location.href}` },
					eventArgs
			  );

		if (hasPageRequestTime) {
			this.push({
				[EventKeys.PageRequestTime]: eventArgs[EventKeys.PageRequestTime],
			});
			delete dL[EventKeys.PageRequestTime];
		}

		if (Object.keys(dL).length) {
			dL[EventKeys.ABTest] = getABFlowGA();
			Object.keys(dL).forEach((key: string) => {
				dL[key] = replaceHtmlTags(dL[key], '');
			});
			this.push(dL);
		}
	}

	sendSimpleEvent(
		category: string,
		action: string,
		label: string,
		pageRequestTime?: number
	) {
		if (pageRequestTime) {
			this.sendEvent({ [EventKeys.PageRequestTime]: pageRequestTime });
		}
		this.sendEvent({
			[EventKeys.EventCategory]: category,
			[EventKeys.EventAction]: action,
			[EventKeys.EventLabel]: label,
		});
	}

	// TODO Configuration and context are passed only to retrieve the eligible services
	// Make those values configurable in the question when no options are allowed
	sendQuestionEvent(
		question: Question,
		choice: string | undefined,
		label: string,
		actualChoice: string | undefined,
		productConfig: ConfigurationModel,
		enrollmentCtxt: EnrollmentContextService
	): void {
		this.sendEvent({
			[EventKeys.PageRequestTime]: new Date().getTime(),
		});

		const customKeyValues = getCustomKeyValues(
			choice,
			actualChoice,
			question,
			productConfig,
			enrollmentCtxt
		);

		const additionalInfo = {
			...customKeyValues,
			[EventKeys.IsSelectedStatus]: choice !== undefined ? 'Success' : 'Error',
		};

		this.sendEvent({
			[EventKeys.EventCategory]: EventCategory.SignUp,
			[EventKeys.EventLabel]: label,
			...additionalInfo,
		});
	}

	// FORM Events
	public sendFocusEvent(label: string): void {
		this.sendSimpleEvent(
			EventCategory.ProfileForm,
			EventAction.FieldFocus,
			label
		);
	}

	public sendChangeEvent(e: Event): void {
		const name = (e.target as HTMLInputElement)?.name;
		if (name) {
			this.sendSimpleEvent(
				EventCategory.ProfileForm,
				EventAction.FieldChange,
				name
			);
		}
	}

	public sendSuccessEvent(buttonLabel: string, eventArgs: EventArgs) {
		this.sendEvent({ [EventKeys.PageRequestTime]: new Date().getTime() });
		this.sendEvent({
			[EventKeys.EventCategory]: EventCategory.ProfileForm,
			[EventKeys.EventAction]: buttonLabel,
			[EventKeys.EventLabel]: EventLabel.Success,
			[EventKeys.ProfileForm]: EventLabel.Success,
			...eventArgs,
		});
	}

	public sendErrors(
		screen: string,
		errors: ErrorArgs | DeepMap<IFormInput, FieldError>,
		errorType?: EventAction.Error | EventAction.OnlineError
	): void {
		const abTest = getABFlowGA();
		for (const key in errors) {
			const values = errors as { [key: string]: { message: string } };

			const eventArgs: FormErrors = {
				[EventKeys.Event]: this.getEventName(),
				[EventKeys.EventCategory]: screen,
				[EventKeys.EventAction]: errorType ?? EventAction.Error,
				[EventKeys.EventLabel]: `${key} - ${values[key].message}`,
				[EventKeys.ErrorForm]: screen,
				[EventKeys.ErrorField]: `${key}`,
				[EventKeys.ErrorName]: `${values[key].message}`,
				[EventKeys.PageURL]: `${window?.location.href}`,
				[EventKeys.ABTest]: abTest,
			};

			this.push(eventArgs);
		}
	}
}
